import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, CalendarClock, UserSquare, MoreHorizontal, LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import Brand from "@/components/Brand";
import NotificationBell from "@/components/NotificationBell";

// Phase 1.18 — mobile-only bottom navigation. Rendered under lg breakpoint.
// Uses env(safe-area-inset-bottom) so it doesn't clip the iOS home indicator.

const CORE_TABS = [
  { to: "/",             label: "Ana",         icon: LayoutDashboard,  testId: "mnav-home",         exact: true },
  { to: "/customers",    label: "Müşteri",     icon: Users,            testId: "mnav-customers" },
  { to: "/appointments", label: "Randevu",     icon: CalendarClock,    testId: "mnav-appointments" },
  { to: "/artists",      label: "Personel",    icon: UserSquare,       testId: "mnav-personnel" },
];

const MORE_ITEMS = [
  { to: "/payouts",               label: "Hakedişler" },
  { to: "/completion-approvals",  label: "Gün Sonu Kontrolü", roles: ["admin", "super_admin"] },
  { to: "/cash-register",         label: "Kasa Raporu", roles: ["admin", "super_admin", "manager"] },
  { to: "/unassigned",            label: "Sahipsiz Kayıtlar", roles: ["admin", "super_admin"] },
  { to: "/segments",              label: "Segmentler" },
  { to: "/reports",               label: "Raporlar" },
  { to: "/satisfactions",         label: "Memnuniyet" },
  // Feb 2026 — nav cleanup: hidden from Daha menu (routes stay live)
  { to: "/audit",                 label: "Aktivite", hidden: true },
  { to: "/import",                label: "Veri Aktar", hidden: true },
  { to: "/settings",              label: "Ayarlar" },
];

export default function MobileBottomNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const filteredMore = MORE_ITEMS.filter(m => !m.hidden && (!m.roles || m.roles.includes(user?.role)));

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <>
      {/* the drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
             onClick={() => setOpen(false)}
             data-testid="mnav-drawer-backdrop">
          <div className="absolute bottom-0 inset-x-0 bg-[#0f0f0f] border-t border-white/10 rounded-t-lg p-4 pb-safe max-h-[75vh] overflow-y-auto"
               style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
               onClick={(e) => e.stopPropagation()}
               data-testid="mnav-drawer">
            <div className="w-10 h-1 rounded-full bg-neutral-700 mx-auto mb-3" />
            <div className="mb-3">
              <div className="text-[9px] uppercase tracking-[0.28em] text-neutral-500 mb-1">Oturum</div>
              <div className="text-sm text-white truncate">{user?.email}</div>
              {user?.role && (
                <div className="text-[9px] text-brand-red uppercase tracking-widest mt-0.5 font-mono">
                  {user.role.replace("_", " ")}
                </div>
              )}
            </div>
            <div className="border-t border-white/5 pt-3">
              <div className="text-[9px] uppercase tracking-[0.28em] text-neutral-500 mb-2">Sayfalar</div>
              <div className="grid grid-cols-2 gap-1.5">
                {filteredMore.map(m => (
                  <NavLink
                    key={m.to}
                    to={m.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mnav-more-${m.to.replace(/\W/g, "")}`}
                    className={({ isActive }) => cn(
                      "flex items-center px-3 py-3 text-sm rounded-sm border border-white/5 min-h-[44px]",
                      isActive ? "bg-brand-red/10 text-white border-brand-red/30" : "text-neutral-300"
                    )}
                  >
                    {m.label}
                  </NavLink>
                ))}
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="mnav-logout"
              className="w-full mt-4 flex items-center justify-center gap-2 px-3 py-3 text-xs uppercase tracking-widest text-neutral-400 border border-white/10 hover:border-brand-red hover:text-brand-red min-h-[44px] rounded-sm"
            >
              <LogOut className="w-3.5 h-3.5" /> Çıkış
            </button>
          </div>
        </div>
      )}

      {/* the bar */}
      <nav
        data-testid="mobile-bottom-nav"
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0a0a0a]/95 backdrop-blur-md border-t border-white/10"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="grid grid-cols-5 items-stretch">
          {CORE_TABS.map(t => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.exact}
              data-testid={t.testId}
              className={({ isActive }) => cn(
                "flex flex-col items-center justify-center py-2 gap-0.5 min-h-[56px]",
                isActive ? "text-brand-red" : "text-neutral-400 hover:text-white"
              )}
            >
              <t.icon className="w-5 h-5" strokeWidth={1.75} />
              <span className="text-[10px] tracking-wide">{t.label}</span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="mnav-more"
            className={cn(
              "flex flex-col items-center justify-center py-2 gap-0.5 min-h-[56px]",
              "text-neutral-400 hover:text-white"
            )}
          >
            <MoreHorizontal className="w-5 h-5" strokeWidth={1.75} />
            <span className="text-[10px] tracking-wide">Daha</span>
          </button>
        </div>
      </nav>

      {/* mobile-only top header (compact) */}
      <div className="lg:hidden sticky top-0 z-20 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/10 flex items-center justify-between px-4 py-3"
           style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
           data-testid="mobile-top-header">
        <Brand variant="compact" href="/" withSubtitle={false} />
        <NotificationBell />
      </div>
    </>
  );
}
